import { Component, Input, OnInit, Output, EventEmitter, SimpleChanges} from '@angular/core';
import { Store } from '@ngrx/store';
import { RouteService } from 'src/app/core/services/route.service';
import { ToggleNavigation } from 'src/app/core/state/_actions/table.actions';
import { Item, Navigation } from 'src/app/core/state/_models/table.models';
import { APP_ROUTE_PATTERN } from '../../constants';
import { CommonService } from 'src/app/core/services/common.service';

@Component({
  selector: 'app-side-navigation',
  templateUrl: './side-navigation.component.html',
  styleUrls: ['./side-navigation.component.scss']
})
export class SideNavigationComponent implements OnInit {
  @Input('navigations') navigations :Array<Navigation> = [];
  @Output() loadDefaultTable = new EventEmitter<any>();

  activeLink: string | null = null;

  isFromLayout:boolean = false;

  constructor(
    private readonly route:RouteService,
    private readonly store: Store,
    private readonly commonService:CommonService
  ) { }

  ngOnInit(): void {
    if(this.navigations.length){
      this.isFromLayout = this.navigations.every((navigation:any)=> navigation.hasOwnProperty('LayoutKey'));
    }

    this.route.change().subscribe(() => {
      this.updateActiveLink(this.route.currentUrl);
    });
  }

  ngOnChanges(changes:SimpleChanges){
    if(changes.navigations.currentValue && changes.navigations.currentValue?.length){
      this.navigations = changes.navigations.currentValue;
      if(this.navigations.length){
        this.isFromLayout = this.navigations.every((navigation:any)=> navigation.hasOwnProperty('LayoutKey'));
        this.updateActiveLink(this.route.currentUrl);
      }
    }
  }

  get isOpenGovTheme() {
    return !this.commonService.isIgnatiusThemeApplied;
  }

  toggleCLicked(navigation:any){
    this.store.dispatch(new ToggleNavigation(this.route.getApplicationKey(), navigation.Order,!navigation.IsExpanded))
  }

   /**
   * @return router link
   */
  getRouterLink = (item: Item) => {
    if (!item?.Href) {
      return '/apps'; // handle blank or undefined Href
    }

    const appRegex = APP_ROUTE_PATTERN;
    let link: Array<string> = [];

    // Case 1: Link is from the same app
    if (this.fromSameApp(item)) {
      const originOrHost = item.Href.includes(window.location.origin)
        ? window.location.origin
        : window.location.host;
      return item.Href.replace(originOrHost, '');
    }

    // Case 2: It's a router link
    if (this.isRouterLink(item.Href)) {
      link = this.getFilteredLink(item.Href);
    } else if (item.Href.includes('http')) {
      // Case 3: External link
      try {
        const url = new URL(item.Href);
        if (appRegex.test(url.pathname)) return url.pathname;
      } catch {
        return '/apps'; // invalid URL
      }
    } else if (appRegex.test(item.Href)) {
      return item.Href;
    } else {
      return '/apps';
    }

    // Case 4: View datatable link
    if (link?.[1] === 'viewdatatable' && item.Title.startsWith('View')) {
      return [
        '/apps',
        link[2],
        'tables',
        link[3],
        link[0] + 's',
        'datatable',
        'view',
        link[4],
      ].join('/');
    }

    // Case 5: Add form link
    if (String(item.Title).startsWith('Add')) {
      return [
        '/apps',
        link[2],
        'tables',
        link[3],
        link[0] + 's',
        'datatable',
        'view',
        link[4],
        'form',
        link[5],
        'add',
      ].join('/');
    }

    // Case 6: Default path
    return [
      '/apps',
      link[1],
      'tables',
      link[2],
      (link[0] === 'tablesettings' ? 'setting' : link[0]) + 's',
    ].join('/');
  };

  getNavItemIcon(iconName: string): string {
    return this.commonService.getNavItemIcon(iconName);
  }

  fromSameApp(item:any){
    if(item.Href.includes(window.location.origin) || item.Href.includes(window.location.host)){
      return true;
    }
    return false;
  }

  openLinkInNewTab(item:any){
    let link = item.Href
    if (!/^https?:\/\//i.test(link)) {
      link = 'http://' + link;
    }
    window.open(link, '_blank');
  }

  isUrl(link:any) {
    const urlPattern = /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;
    return urlPattern.test(link) || /^[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/.test(link);
  }

  isRouterLink(link:any) {
    const routerLinkPattern = /^\/[^\/\s]+/;
    return routerLinkPattern.test(link);
  }

  openLink(item: any) {
    let url = item.Href
    if (!/^https?:\/\//i.test(url)) {
      url = 'http://' + url;
    }
    if(!this.isRouterLink(url)){
      const app_regex = APP_ROUTE_PATTERN;
      const newUrl = new URL(url);
      if(app_regex.test(newUrl.pathname)){
        this.route.navigate([newUrl.pathname]);
        return;
      }
    }
    window.location.href = url;
  }

  getFilteredLink(link:any){
    const array = link.split('/');

    const unwantedPattern = /^[#!]+$/;

    const filteredArray = array.filter((item:any) => !unwantedPattern.test(item));

    return filteredArray.filter((item:any)=> !!item);

  }

  getNavigationHref(navigation:any){
    return `#TableId-${navigation?.LevelIds?.TableId || navigation.Order}`
  }

  updateActiveLink(currentUrl: string) {
    const routeExists = this.navigations.some(nav =>
      nav.Items.some(i => this.getRouterLink(i) === currentUrl)
    );

    if (routeExists) {
      this.activeLink = currentUrl;
      return;
    }

    const matchingItem = this.navigations
      .map(nav => nav.Items)
      .reduce((acc, items) => acc.concat(items), [])
      .filter(i => currentUrl.startsWith(this.getRouterLink(i)))
      .sort((a, b) => this.getRouterLink(b).length - this.getRouterLink(a).length)[0];

    this.activeLink = matchingItem ? this.getRouterLink(matchingItem) : null;
  }
}
